import { CodeIcon, CompassIcon, SparklesIcon } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

export interface WelcomeScreenProps {
  onSuggestionClick?: (suggestion: string) => void;
  className?: string;
}

const GENERAL_SUGGESTIONS = [
  "Explain a complex topic in simple terms",
  "Help me brainstorm ideas for my project",
  "Answer questions about any subject",
  "Assist with problem-solving and decision making",
];

const CATEGORIES = [
  {
    id: "analyze",
    name: "Analyze",
    icon: CompassIcon,
    suggestions: [
      "Break down complex problems step by step",
      "Compare different options and approaches",
      "Analyze data and identify patterns",
      "Evaluate pros and cons of decisions",
    ],
  },
  {
    id: "generate",
    name: "Create",
    icon: SparklesIcon,
    suggestions: [
      "Write creative content and stories",
      "Generate ideas for projects or businesses",
      "Create outlines and structured plans",
      "Draft emails and professional documents",
    ],
  },
  {
    id: "refactor",
    name: "Learn",
    icon: CodeIcon,
    suggestions: [
      "Explain technical concepts clearly",
      "Provide step-by-step tutorials",
      "Research and summarize topics",
      "Answer questions about any subject",
    ],
  },
] as const;

const SuggestionItem = ({
  suggestion,
  index,
  totalLength,
  onSuggestionClick,
}: {
  suggestion: string;
  index: number;
  totalLength: number;
  onSuggestionClick?: (suggestion: string) => void;
}) => (
  <div key={`suggestion-${index}`}>
    <button
      type="button"
      className="w-full py-3 text-left text-sm hover:text-primary cursor-pointer transition-colors"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSuggestionClick?.(suggestion);
      }}
    >
      {suggestion}
    </button>
    {index < totalLength - 1 && <div className="border-b border-border/50" />}
  </div>
);

export function WelcomeScreen({ onSuggestionClick, className }: WelcomeScreenProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("general");

  const currentSuggestions =
    selectedCategory === "general"
      ? GENERAL_SUGGESTIONS
      : (CATEGORIES.find((cat) => cat.id === selectedCategory)?.suggestions ?? []);

  return (
    <div className={cn("w-full space-y-6 px-2 sm:px-8", className)}>
      <h1 className="text-3xl font-semibold text-center">What can I help you with today?</h1>

      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Button
          variant={selectedCategory === "general" ? "default" : "outline"}
          size="sm"
          className="rounded-full"
          onClick={() => setSelectedCategory("general")}
        >
          General
        </Button>

        {CATEGORIES.map((category) => {
          const Icon = category.icon;
          return (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              size="sm"
              className="rounded-full gap-2"
              onClick={() => setSelectedCategory(category.id)}
            >
              <Icon size={14} />
              {category.name}
            </Button>
          );
        })}
      </div>

      <div
        key={selectedCategory}
        className="animate-in fade-in slide-in-from-bottom-2 duration-200"
      >
        {currentSuggestions.map((suggestion: string, index: number) => (
          <SuggestionItem
            key={`${selectedCategory}-${index}`}
            suggestion={suggestion}
            index={index}
            totalLength={currentSuggestions.length}
            onSuggestionClick={onSuggestionClick}
          />
        ))}
      </div>
    </div>
  );
}
